import { DeactivatableButton } from '@folio/components';
import { useToast } from '@folio/components/ui/use-toast';
import { useAssertedParams } from '@folio/lib';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useMutation } from '@tanstack/react-query';
import {
  brokerageFirmDtoFirmTypeEnum,
  policyControllerConfirmPolicy,
  usePolicyControllerGeneratePolicySignatureRequest,
} from 'korint-sdk';
import { MailIcon } from 'lucide-react';
import { useFinalizeNewBusinessRules } from '../useFinalizeNewBusinessRules';
import { useTranslation } from 'react-i18next';
import { offlineAwarePaymentMethodRule } from '@folio/utils/offlineAwarePaymentMethodRule';
import { useVisibilityConditionsProps } from '../useVisibilityConditionsProps';

export function QuicksignButton({ overridenBranchId }: { overridenBranchId?: string }) {
  const { t } = useTranslation();

  const { toast } = useToast();
  const { policyId, branchId: assertedBranchId } = useAssertedParams();
  const branchId = overridenBranchId ?? assertedBranchId;
  const { mutate: refetchPolicyOnDashboard } = usePolicyOnDashboard();

  const visibilityConditionsProps = useVisibilityConditionsProps({
    action: 'CONFIRM',
    overridenBranchId,
  });

  const { mutateAsync: generatePolicySignatureRequest } =
    usePolicyControllerGeneratePolicySignatureRequest({
      mutation: {
        // biome-ignore lint/suspicious/noExplicitAny: typing problem for error
        retry: (failureCount: number, error: any) =>
          error?.errorResponse?.status === 425 && failureCount < 100,
        retryDelay: () => 1500,
      },
    });

  const { mutate: sendQuicksign, isPending } = useMutation({
    mutationFn: async () => {
      if (visibilityConditionsProps?.policyStatus !== 'POLICY_CONFIRMED') {
        await policyControllerConfirmPolicy(policyId, { branchId });
      }
      await generatePolicySignatureRequest({
        policyId,
        data: { deliveryMode: 'EMAIL', isOtp: false },
        params: { branchId },
      });
    },
    onSuccess: async () => {
      toast({
        title: t([
          'finalizeNewBusiness.buttons.quick-sign.successMessage',
          'finalizeNewBusiness.defaultSuccessMessage',
        ]),
      });
      await refetchPolicyOnDashboard();
    },
  });

  const rules = useFinalizeNewBusinessRules({
    additionnalRules: [
      { id: 'card-payment', isValid: offlineAwarePaymentMethodRule({ requiredType: 'card' }) },
    ],
    overridenBranchId,
  });

  if (!rules) {
    return null;
  }

  if (!visibilityConditionsProps) {
    return null;
  }

  const showButton =
    visibilityConditionsProps.firmType === brokerageFirmDtoFirmTypeEnum.WHOLESALE &&
    visibilityConditionsProps.isDerogationSatisfied &&
    visibilityConditionsProps.branchStatus !== 'BRANCH_CLOSED' &&
    visibilityConditionsProps.policyStatus === 'POLICY_QUOTED';

  if (!showButton) {
    return null;
  }

  return (
    <DeactivatableButton
      variant="ghost"
      rules={rules}
      icon={<MailIcon className="h-4 w-4" />}
      isLoading={isPending}
      onClick={() => sendQuicksign()}
    >
      <span>{t('finalizeNewBusiness.buttons.quick-sign.label')}</span>
    </DeactivatableButton>
  );
}
